'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { EmptyState, NoticeBanner } from '@/shared/ui';
import { TrashCard } from './TrashCard';
import type { TrashItem } from '../model/trash.types';

interface TrashListProps {
  items: TrashItem[];
  /** Server-composed explanation of the deletion window; null while loading. */
  notice: string | null;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  onRestore: (item: TrashItem) => void;
  restoringId?: string | null;
}

export function TrashList({
  items,
  notice,
  isLoading = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  fetchNextPage,
  onRestore,
  restoringId = null,
}: TrashListProps) {
  const t = useTranslations('history.trash');
  const tHistory = useTranslations('history');
  const router = useRouter();
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!fetchNextPage || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleGoToRecent = useCallback(() => {
    router.push('/history');
  }, [router]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-4 md:gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-48 rounded-lg" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Trash2 />}
        title={t('empty.title')}
        description={t('empty.description')}
        action={{ label: t('empty.button'), onClick: handleGoToRecent }}
        centered
      />
    );
  }

  return (
    <>
      {notice && (
        <NoticeBanner variant="info" message={notice} className="mb-4" />
      )}

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-4 md:gap-6">
        {items.map((item) => (
          <TrashCard
            key={`${item.type}-${item.id}`}
            item={item}
            onRestore={onRestore}
            isRestoring={restoringId === `${item.type}-${item.id}`}
          />
        ))}
      </div>

      {hasNextPage && (
        <div ref={loadMoreRef} className="flex justify-center py-4">
          {isFetchingNextPage ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : (
            <Button
              variant="outline"
              onClick={fetchNextPage}
              disabled={!hasNextPage || isFetchingNextPage}
            >
              {tHistory('loadMore')}
            </Button>
          )}
        </div>
      )}
    </>
  );
}
